'use client'

import { useEffect, useState } from 'react'

import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { motion } from 'framer-motion'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Clock,
  CreditCard,
  FileText,
  Shield,
  Users,
} from 'lucide-react'

import { Registration } from '../../lib/workshop-api'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'

const stripePublishableKey =
  typeof process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY === 'string'
    ? process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY.trim()
    : ''

/** Only call loadStripe with a non-empty key: empty string throws IntegrationError. */
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null

interface PaymentFormProps {
  registration: Registration | null
  paymentClientSecret: string | null
  onPaymentMethodSelect: (method: 'stripe' | 'xero') => void
  onPaymentSuccess: () => void
  onPaymentFailure: (error: string) => void
  onJoinWaitlist: () => void
  loading?: boolean
}

function StripePaymentForm({
  clientSecret,
  onSuccess,
  onFailure,
  loading,
}: {
  clientSecret: string
  onSuccess: () => void
  onFailure: (error: string) => void
  loading?: boolean
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setProcessing(true)
    setError(null)

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setError('Card element not found')
      setProcessing(false)
      return
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    })

    if (error) {
      setError(error.message || 'Payment failed')
      onFailure(error.message || 'Payment failed')
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess()
    }

    setProcessing(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-highest p-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#dce1ff',
                '::placeholder': {
                  color: '#849495',
                },
              },
            },
          }}
        />
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || processing || loading}
        variant="kinetic"
        className="w-full"
        size="lg"
      >
        {processing ? (
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span>Processing Payment...</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Pay with Card</span>
          </div>
        )}
      </Button>
    </form>
  )
}

export function PaymentForm({
  registration,
  paymentClientSecret,
  onPaymentMethodSelect,
  onPaymentSuccess,
  onPaymentFailure,
  onJoinWaitlist,
  loading = false,
}: PaymentFormProps) {
  const [selectedMethod, setSelectedMethod] = useState<'stripe' | 'xero' | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)

  const handleMethodSelect = (method: 'stripe' | 'xero') => {
    setSelectedMethod(method)
    onPaymentMethodSelect(method)

    if (method === 'stripe') {
      setShowPaymentForm(true)
    } else {
      // For Xero, we proceed directly (invoice will be generated)
      setShowPaymentForm(false)
    }
  }

  const getWorkshopInfo = () => {
    if (!registration?.selectedWorkshops?.length) return null

    // For now, we'll use the first selected workshop
    // In a real implementation, this would be the assigned workshop
    return registration.selectedWorkshops[0]
  }

  const workshopInfo = getWorkshopInfo()
  const estimatedPrice = workshopInfo?.format === 'in-person' ? 500 : 300 // Placeholder pricing

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 text-center shadow-none">
        <div className="bg-kinetic-surface-container-highest/60 border-kinetic-outline-variant/30 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-none border">
          <Check className="h-8 w-8 text-kinetic-secondary-fixed" />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-kinetic-secondary-fixed">
          PAYMENT_GATE
        </p>
        <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Workshop Available!
        </h2>
        <p className="mt-2 text-kinetic-on-surface-variant">
          Great news! We found an available spot in your preferred workshop.
        </p>
      </Card>

      {/* Workshop Assignment Details */}
      {workshopInfo && (
        <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 shadow-none">
          <h3 className="mb-4 font-headline text-lg font-bold uppercase tracking-tight text-white">
            Your Workshop Assignment
          </h3>

          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-lg font-medium">{workshopInfo.title}</h4>
                <div className="mt-2 flex items-center space-x-3">
                  <Badge className="capitalize">{workshopInfo.format}</Badge>
                  <Badge variant="outline">Preference #{workshopInfo.preference}</Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-kinetic-primary-container">
                  ${estimatedPrice}
                </div>
                <div className="text-sm text-kinetic-on-surface-variant">
                  {workshopInfo.format} format
                </div>
              </div>
            </div>

            {/* Workshop details would go here */}
            <div className="bg-kinetic-surface-container-highest/40 border-kinetic-outline-variant/20 rounded-none border p-4">
              <h5 className="mb-2 font-headline font-bold text-white">What's included:</h5>
              <ul className="space-y-1 text-sm text-kinetic-on-surface-variant">
                <li>• Expert-led workshop sessions</li>
                <li>• Hands-on practical exercises</li>
                <li>• Workshop materials and resources</li>
                <li>• Certificate of completion</li>
                <li>• Follow-up support and community access</li>
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Payment Method Selection */}
      {!selectedMethod && (
        <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 shadow-none">
          <h3 className="mb-4 font-headline text-lg font-bold uppercase tracking-tight text-white">
            Choose Payment Method
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Stripe Payment */}
            <div
              className={`border-kinetic-outline-variant/30 bg-kinetic-surface-container-highest/30 rounded-none border p-4 ${
                stripePromise
                  ? 'cursor-pointer transition-colors hover:border-kinetic-primary-container'
                  : 'cursor-not-allowed opacity-60'
              }`}
              onClick={() => stripePromise && handleMethodSelect('stripe')}
              role="button"
              tabIndex={stripePromise ? 0 : -1}
              onKeyDown={e => {
                if (stripePromise && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  handleMethodSelect('stripe')
                }
              }}
            >
              <div className="mb-3 flex items-center space-x-3">
                <CreditCard className="h-6 w-6 text-kinetic-primary-container" />
                <h4 className="font-headline font-bold text-white">Pay with Card</h4>
              </div>
              <p className="mb-3 text-sm text-kinetic-on-surface-variant">
                Secure payment with Visa, Mastercard, or American Express
              </p>
              <div className="flex items-center space-x-2 text-sm text-kinetic-secondary-fixed">
                <Check className="h-4 w-4" />
                <span>Instant confirmation</span>
              </div>
              {!stripePromise && (
                <p className="mt-3 font-mono text-xs text-kinetic-outline">
                  Unavailable: set NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY for local dev.
                </p>
              )}
            </div>

            {/* Xero Invoice */}
            <div
              className="border-kinetic-outline-variant/30 bg-kinetic-surface-container-highest/30 cursor-pointer rounded-none border p-4 transition-colors hover:border-kinetic-primary-container"
              onClick={() => handleMethodSelect('xero')}
            >
              <div className="mb-3 flex items-center space-x-3">
                <FileText className="h-6 w-6 text-kinetic-primary-container" />
                <h4 className="font-headline font-bold text-white">Request Invoice</h4>
              </div>
              <p className="mb-3 text-sm text-kinetic-on-surface-variant">
                Receive an invoice for payment by bank transfer
              </p>
              <div className="flex items-center space-x-2 text-sm text-kinetic-outline">
                <Clock className="h-4 w-4" />
                <span>2-3 business days processing</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stripe Payment Form */}
      {selectedMethod === 'stripe' && showPaymentForm && paymentClientSecret && (
        <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 shadow-none">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-headline text-lg font-bold uppercase tracking-tight text-white">
              Complete Payment
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedMethod(null)
                setShowPaymentForm(false)
              }}
              className="rounded-none"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>

          {stripePromise ? (
            <>
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  clientSecret={paymentClientSecret}
                  onSuccess={onPaymentSuccess}
                  onFailure={onPaymentFailure}
                  loading={loading}
                />
              </Elements>
              <div className="bg-kinetic-surface-container-highest/40 border-kinetic-outline-variant/20 mt-4 rounded-none border p-3">
                <div className="flex items-center space-x-2 text-sm text-kinetic-on-surface-variant">
                  <Shield className="h-4 w-4" />
                  <span>Your payment is secured with 256-bit SSL encryption</span>
                </div>
              </div>
            </>
          ) : (
            <div className="border-kinetic-outline-variant/30 bg-kinetic-surface-container-highest/40 rounded-none border p-4 text-sm text-kinetic-on-surface-variant">
              <p className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-kinetic-outline" />
                <span>
                  Card payments are not configured (missing{' '}
                  <code className="font-mono text-xs text-kinetic-on-surface">
                    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
                  </code>
                  ). Use <strong className="text-kinetic-on-surface">Request Invoice</strong>{' '}
                  instead, or add the key to your{' '}
                  <code className="font-mono text-xs">.env.local</code> and restart the dev server.
                </span>
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Xero Invoice Confirmation */}
      {selectedMethod === 'xero' && (
        <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 shadow-none">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-headline text-lg font-bold uppercase tracking-tight text-white">
              Invoice Request
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedMethod(null)}
              className="rounded-none"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>

          <div className="space-y-4">
            <div className="bg-kinetic-surface-container-highest/40 border-kinetic-outline-variant/20 rounded-none border p-4">
              <h4 className="mb-2 font-headline font-bold text-white">What happens next?</h4>
              <ul className="space-y-1 text-sm text-kinetic-on-surface-variant">
                <li>• We'll generate an invoice for your workshop registration</li>
                <li>• You'll receive the invoice via email within 24 hours</li>
                <li>• Payment terms are typically 14 days from invoice date</li>
                <li>• Your workshop spot is reserved upon invoice generation</li>
                <li>• Workshop access will be provided once payment is received</li>
              </ul>
            </div>

            <Button
              onClick={onPaymentSuccess}
              disabled={loading}
              size="lg"
              variant="kinetic"
              className="w-full"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Generating Invoice...</span>
                </div>
              ) : (
                'Request Invoice'
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Alternative: Join Waitlist */}
      <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6 shadow-none">
        <div className="flex items-start space-x-3">
          <Users className="mt-0.5 h-5 w-5 text-kinetic-outline" />
          <div className="flex-1">
            <h4 className="font-headline font-bold uppercase tracking-tight text-white">
              Prefer to wait for other options?
            </h4>
            <p className="mt-1 text-sm text-kinetic-on-surface-variant">
              If this workshop doesn't meet your needs, you can join our waitlist to be notified
              when other workshops become available.
            </p>
            <Button
              variant="outline"
              onClick={onJoinWaitlist}
              className="border-kinetic-outline-variant/50 mt-3 rounded-none text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
            >
              Join Waitlist Instead
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  )
}
